import { useContext } from 'react';
import { AppContext } from '@ctsu/loader';
import _ from 'lodash';
import {
  OpenErrorNotification, OpenNotification, OpenSuccessNotification, OpenWarningNotification
} from '@ctsu/react-ctsu-ui';
import { protocolData, irbExemptData, irbStatusData, siteData } from 'appconstants/SummaryBarConstants';
import { vsprintf } from 'sprintf-js';
import {
  addYears, format, getHours, getMinutes, getSeconds, isAfter, isBefore, isToday
} from 'date-fns';
import { format as dateZformate } from 'date-fns-tz';

const DATE_FORMAT = 'dd-MMM-yyyy';
const DATE_TIME_FORMAT_TIME_ZONE = 'dd-MMM-yyyy hh:mm:ss a zzz';
const TIME_FORMAT = 'hh:mm:ss a';
const DATE_TIME_UAT = 'dd-MMM-yyyy hh:mm a zzz';
const TIMEZONE = 'America/New_York';
const CAP_QUEUED_DATE_FORMAT = 'dd-MMM-yyyy hh:mm:ss a';

export const dateFormatter = (date, dtFormat) => format(new Date(date), dtFormat);

export const dateZoneFormatter = (date, dtFormat) => dateZformate(new Date(date), dtFormat, { timeZone: TIMEZONE });

export const getUIDateFormat = inputDt => (inputDt ? new Date(dateFormatter(inputDt, DATE_FORMAT)) : '');

export const getApiDateFormat = inputDt => (inputDt ? dateFormatter(inputDt, DATE_FORMAT) : '');

export const getUIDTFormat = inputDt => (inputDt ? new Date(dateFormatter(inputDt, DATE_TIME_UAT)) : '');

export const getUIDateTimeFormat = d => (!d ? '' : dateFormatter(d, CAP_QUEUED_DATE_FORMAT));

export const getApiDTFormat = inputDt => (inputDt ? dateZoneFormatter(inputDt, DATE_TIME_FORMAT_TIME_ZONE) : '');

export const formatDate = date => (date != null ? dateFormatter(date, DATE_FORMAT) : null);
export const toDate = date => (date != null ? new Date(dateFormatter(date, DATE_FORMAT)) : null);
export const toDateTime = date => (date != null ? new Date(dateFormatter(date, DATE_TIME_UAT)) : null);
export const toTime = date => {
  if (date != null) {
    const z = dateFormatter(date, TIME_FORMAT);
    return `${z} EDT`;
  }
  return null;
};
export const toFullDateTime = date => {
  if (date != null) {
    return new Date(dateZoneFormatter(date, DATE_TIME_FORMAT_TIME_ZONE));
  }
  return null;
};

export function useCtxParseValue(key) {
  const context = useContext(AppContext);
  return (
    key
    && context
    && context.application
    && context.application.appConfig[key]
    && JSON.parse(context.application.appConfig[key])
  );
}

export function useCtxValue(key) {
  const context = useContext(AppContext);
  return context && context.application && context.application.appConfig[key];
}

export function GetFromCtxValue(context, key) {
  return context && context.application && context.application.appConfig[key];
}

export const getSessionByKey = (key) => (key ? sessionStorage.getItem(key) : '');
export const setSessionForKey = (key, value) => key && value && sessionStorage.setItem(key, value);

export function getEmptyValue(value) {
  return value || '';
}

export function getTableColumns(cols) {
  return cols.map((col) => ({
    name: col[0],
    type: col[1],
    label: col[2],
    align: col[3],
    filterable: col[4]
  }));
}

export function checkBool(val) {
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  return val || '';
}

export function doPassThrough(app, param) {
  const url = `${process.env.REACT_APP_ORIGIN}core-service/system/core/auth/REGULATORY/relay/${app}?relayState=${param}`;
  window.open(url, '_blank');
}

export function isAfterDate(date, dateToCompare) {
  return isAfter(new Date(date), new Date(dateToCompare));
}

export function isBeforeDate(date, dateToCompare) {
  return isBefore(new Date(date), new Date(dateToCompare));
}

export function isInYear(date, dateToCompare) {
  const d2 = addYears(new Date(dateToCompare), 1);
  return !isBefore(new Date(date), new Date(dateToCompare)) && !isAfter(new Date(date), new Date(d2));
}

export const onEnterKeyPress = (eve, handler) => {
  if (eve != null && eve.keyCode === 13) {
    handler();
    eve.preventDefault();
  }
};

export const displayBooleanAsYN = (val) => {
  if (_.isBoolean(val)) {
    return val ? 'Yes' : 'No';
  }
  return null;
};

export const JUSTIFICATION_OPTIONS = [
  '1',
  '1a',
  '1b',
  '2',
  '2a',
  '2b',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8a',
  '8b',
  '8c',
  '9',
  'Allowed per LPO'
].map((n) => ({
  value: n,
  label: n
}));

export const createMarkup = (data) => ({ __html: data.replace(/\n/g, '<br />') });

export function getQuery(location) {
  return new URLSearchParams(location.search);
}

export const getDecodedQueryParams = (location) => {
  const query = getQuery(location);
  const q = query.get('q');
  const params = atob(q);
  return new URLSearchParams(params);
};

export const getEncodedQueryParams = (urlPath, params) => {
  const encodedData = btoa(params);
  return `${urlPath}?q=${encodedData}`;
};

export const getEncodedWithoutQueryParams = (urlPath, params) => {
  const encodedData = btoa(params);
  return `${urlPath}${encodedData}`;
};

export function usePathName(location) {
  return location.pathname;
}

export const calculatePIStartDate = (date, defaultMin = 0) => {
  if (date != null) {
    const today = new Date();
    if (isToday(new Date(date))) {
      const hrs = getHours(new Date(date));
      const mins = getMinutes(new Date(date));
      const secs = getSeconds(new Date(date));
      if (hrs === 0 && mins === 0 && secs === 0) {
        date.setHours(today.getHours());
        date.setMinutes(today.getMinutes());
        date.setSeconds(today.getSeconds());
      } else {
        date.setHours(hrs);
        date.setMinutes(mins);
        date.setSeconds(secs);
      }
    } else {
      date.setHours(0);
      date.setMinutes(defaultMin);
      date.setSeconds(0);
    }
    return getApiDTFormat(date);
  }
  return null;
};

export const compareDates = (d1, d2) => {
  if (d1 != null && d2 != null) {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    if (date1 === date2) {
      return 0;
    }
    return date1 > date2 ? -1 : 1;
  }
  return d2 != null ? -1 : 1;
};

export const onEnterKeyPressed = (e, fn) => e.which === 13 && fn();

/**
 * extracts ctep id from eg Morgan, Elaine Ruth(IVR-7586)
 * @param {*} piName
 * @returns {number} ctepid
 */
export const getCtepIdFromPIName = (piName) => {
  let nameArr = [];
  let ctepId = null;
  if (piName && piName.length > 0) {
    nameArr = piName.split('-');
    ctepId = nameArr[nameArr.length - 1];
    ctepId = ctepId.slice(0, -1);
  }
  return ctepId;
};

export const useUsername = () => {
  const context = useContext(AppContext);
  const { session } = context;
  return session && session.username ? session.username : null;
};

export const GetUsername = () => useUsername();

export const showSuccessNotification = (message, displayTimeInSec = 15) => {
  OpenSuccessNotification({
    message,
    autoHide: true,
    displayTimeInSec
  });
};
export const showErrorNotification = (message, displayTimeInSec = 15) => {
  OpenErrorNotification({
    message,
    autoHide: true,
    displayTimeInSec
  });
};

export const showInfoNotification = (message, displayTimeInSec = 15) => {
  OpenNotification({
    message,
    autoHide: true,
    displayTimeInSec
  });
};

export const getGAId = () => {
  const GACode = type => {
    try {
      return JSON.parse(process.env.REACT_APP_GA)[type];
    } catch (e) {
      return '';
    }
  };
  const websiteEnv = window.location.hostname;
  let gaTrackingId = '';
  if (websiteEnv.search('unit-') >= 0) {
    gaTrackingId = GACode('UNIT');
  } else if (websiteEnv.search('uat-') >= 0) {
    gaTrackingId = GACode('UAT');
  } else if (websiteEnv.search('ci-') >= 0 || websiteEnv.search('localhost') >= 0) {
    gaTrackingId = GACode('CI');
  } else if (websiteEnv.search('test-') >= 0) {
    gaTrackingId = GACode('TEST');
  } else {
    gaTrackingId = GACode('PROD');
  }
  return gaTrackingId;
};

export const stringHelper = (message, params = []) => vsprintf(message, params);

export const showWarningNotification = (message, displayTimeInSec = 15) => {
  OpenWarningNotification({
    message,
    autoHide: true,
    displayTimeInSec
  });
};

export const useAppConfig = () => {
  const context = useContext(AppContext);
  const { application } = context;
  const { appConfig } = application;
  return appConfig;
};

export const getCapSummaryData = (data) => {
  const otherData =  data?.isIrbExempt ? irbExemptData : irbStatusData;
  return [ ...protocolData, ...otherData ];
};

export const getCommonSummaryData = (data) => {
  const otherData = data?.isIrbExempt ? irbExemptData : irbStatusData;
  return [ ...siteData, ...protocolData, ...otherData ];
};
// replaceAll not supported in node14
export const getExportTitle = (title) => title.split(' ').join('-');